import { CircularProgress } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
import { selectCurrentUser } from "../store/reducers/auth/authSlice";
import MainLayout from "./layout/MainLayout";
import CertificatesPage from "./pages/Certificates/CertificatesPage";
import ClassDetailPage from "./pages/Classes/ClassDetailPage";
import ClassesPage from "./pages/Classes/ClassesPage";
import StudentPage from "./pages/Classes/StudentPage";
import DistrictPage from "./pages/Districts/DistrictPage";
import DistrictsPage from "./pages/Districts/DistrictsPage";
import RosteringPage from "./pages/Districts/RosteringPage";
import MfaEnrollPage from "./pages/Login/MfaEnrollPage";
import PeoplePage from "./pages/People/PeoplePage";
import Profile from "./pages/Profile/Profile";
import ClassReportPage from "./pages/Reports/ClassReportPage";
import ReportsPage from "./pages/Reports/ReportsPage";
import {
  DistrictReportPage,
  SchoolReportPage,
} from "./pages/Reports/ScopeReportPage";
import StudentResultPage from "./pages/Results/StudentResultPage";
import SessionReviewPage from "./pages/Review/SessionReviewPage";
import VideoReviewPage from "./pages/Review/VideoReviewPage";
import SchoolPage from "./pages/Schools/SchoolPage";
import SessionDetailPage from "./pages/Sessions/SessionDetailPage";
import SessionPlannerPage from "./pages/Sessions/SessionPlannerPage";
import SessionsListPage from "./pages/Sessions/SessionsListPage";
import DataRequestsPage from "./pages/Settings/DataRequestsPage";
import PrivacySettingsPage from "./pages/Settings/PrivacySettingsPage";
import TodayPage from "./pages/Today/TodayPage";
import PrivateRoute from "./privateRoute";
import GradingRoutes from "./routes/GradingRoutes";
import { ROSTERING_ACCESS, permissions } from "./utils/permissionsHelpers";

const withLayout = (element: JSX.Element) => <MainLayout>{element}</MainLayout>;

export function GradingHome() {
  const match = useRouteMatch();
  const [error, setError] = useState("");
  const user = useSelector(selectCurrentUser);

  return (
    <Switch>
      <PrivateRoute path={`${match.path}/today`} exact>
        {withLayout(<TodayPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/sessions/plan`} exact>
        {withLayout(<SessionPlannerPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/sessions/:sessionId/certificates`}>
        {withLayout(<CertificatesPage />)}
      </PrivateRoute>
      <PrivateRoute
        path={`${match.path}/sessions/:sessionId/results/:studentId`}
      >
        {withLayout(<StudentResultPage />)}
      </PrivateRoute>
      <PrivateRoute
        path={`${match.path}/sessions/:sessionId/entries/:entryId/video`}
      >
        {withLayout(<VideoReviewPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/sessions/:sessionId/review`}>
        {withLayout(<SessionReviewPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/sessions/:sessionId`}>
        {withLayout(<SessionDetailPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/sessions`}>
        {withLayout(<SessionsListPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/classes/:classId`}>
        {withLayout(<ClassDetailPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/classes`}>
        {withLayout(<ClassesPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/people/:tab?`}>
        {withLayout(<PeoplePage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/students/:studentId`}>
        {withLayout(<StudentPage />)}
      </PrivateRoute>
      <PrivateRoute
        path={`${match.path}/districts/:districtId/privacy`}
        permission={permissions.MANAGE_PRIVACY}
      >
        {withLayout(<PrivacySettingsPage />)}
      </PrivateRoute>
      <PrivateRoute
        path={`${match.path}/districts/:districtId/data-requests`}
        permission={permissions.MANAGE_DATA_REQUESTS}
      >
        {withLayout(<DataRequestsPage />)}
      </PrivateRoute>
      <PrivateRoute
        path={`${match.path}/districts/:districtId/rostering`}
        permission={ROSTERING_ACCESS}
      >
        {withLayout(<RosteringPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/districts/:districtId`}>
        {withLayout(<DistrictPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/districts`}>
        {withLayout(<DistrictsPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/schools/:schoolId`}>
        {withLayout(<SchoolPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/reports/classes/:classId`}>
        {withLayout(<ClassReportPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/reports/schools/:schoolId`}>
        {withLayout(<SchoolReportPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/reports/districts/:districtId`}>
        {withLayout(<DistrictReportPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/reports`}>
        {withLayout(<ReportsPage />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/profile`} exact>
        {withLayout(<Profile />)}
      </PrivateRoute>
      <PrivateRoute path={`${match.path}/mfa`} exact>
        <MfaEnrollPage />
      </PrivateRoute>
      <Route path={`${match.path}`}>
        {user === undefined ? (
          <CircularProgress
            style={{
              left: "50%",
              top: "50%",
              position: "absolute",
              marginLeft: -8,
            }}
          />
        ) : user === null ? (
          <GradingRoutes error={error} setError={setError} />
        ) : (
          <Redirect to={`${match.path}/today`} />
        )}
      </Route>
    </Switch>
  );
}
